import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import UserEmailDuplicateKeepCell from "@/v2-backoffice-client/user/emailDuplicate/UserEmailDuplicateKeepCell"
import { useUserEmailDuplicateQuery } from "@/v2-backoffice-client/user/emailDuplicate/user.emailDuplicate.query"
import {
  describeDuplicateLogins,
  describeDuplicateOrigin,
  groupDuplicatesByEmail,
  suggestKeeper,
} from "@/v2-backoffice-client/user/emailDuplicate/userEmailDuplicate.utils"
import type { UserEmailDuplicateFragment } from "@/v2-backoffice-client/user/emailDuplicate/__types__/user.emailDuplicate.fragment"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"

const COLUMN_SIZING = {
  Email: 240,
  User: 170,
  Id: 70,
  Logins: 150,
  LastSignInAt: 120,
  Tenants: 80,
  Seats: 70,
  Origin: 220,
  Hubspot: 90,
  Keep: 150,
}

const KEEP_DESCRIPTION =
  "One account per email. Each row's button keeps that row and merges the other rows of the email into it: their teams, their named seats, the login the kept row lacks (Fusion or B2C) and the HubSpot link follow the person, and the merged rows are removed, on v1 and v2. The filled button is the row the board suggests: the one the person signed in with, else the one with the most teams, else the oldest."

type CreateColumnsInput = {
  /** The other live rows of a row's email, by row id. */
  othersById: Map<number, UserEmailDuplicateFragment[]>
  /** The suggested keeper's id per email. */
  suggestedIds: Set<number>
  onMerged(): void
}

function createUserEmailDuplicateColumns(input: CreateColumnsInput) {
  const { othersById, suggestedIds, onMerged } = input
  return createColumns<UserEmailDuplicateFragment>({
    enableSelection: false,
    enableSorting: true,
    enableHiding: true,
    columns: {
      Email: {
        id: "Email",
        label: "Email",
        enableGrouping: true,
        filter: { type: "text", placeholder: "Email..." },
        getValue: (row) => row.Email.toLowerCase(),
      },
      User: {
        id: "User",
        label: "User",
        filter: { type: "text", placeholder: "Name..." },
        getValue: (row) => formatUserName(row.FirstName, row.LastName),
        to: ({ row }) => `?v=user:${row.original.Id}`,
      },
      Id: {
        id: "Id",
        label: "Id",
        getValue: (row) => row.Id,
      },
      Logins: {
        id: "Logins",
        label: "Logins",
        description:
          "Which directory the row can sign in through. A Fusion login is an Entra External ID account; a B2C login is the legacy one, used by v1 and by invitation links that still go through the old register page.",
        enableGrouping: true,
        getValue: (row) => describeDuplicateLogins(row),
        renderCell({ row }) {
          const label = describeDuplicateLogins(row.original)
          const variant = row.original.HasEntraId ? "success-1" : "muted"
          return <DotText variant={variant}>{label}</DotText>
        },
      },
      LastSignInAt: {
        id: "LastSignInAt",
        label: "Signed In",
        description:
          "The last sign-in the directory logged for this row; a row that never signed in is the one that is usually merged away.",
        getValue: (row) => row.LastSignInAt,
        renderCell({ row }) {
          const value = row.original.LastSignInAt
          return value ? formatDateString(value) : "Never"
        },
        exportValue({ row }) {
          return row.original.LastSignInAt
            ? formatDateString(row.original.LastSignInAt)
            : "Never"
        },
      },
      Tenants: {
        id: "Tenants",
        label: "Teams",
        getValue: (row) => row.TenantCount,
      },
      Seats: {
        id: "Seats",
        label: "Seats",
        description:
          "Named seats the row holds on limited licenses (Placepoint MCP). They follow onto the row you keep.",
        getValue: (row) => row.SeatCount,
      },
      Origin: {
        id: "Origin",
        label: "Created",
        description:
          "Where the row came from: a sign-up through an invitation, or a Fusion login created in the Backoffice (by which operator). Both for one email is how every duplicate here arose.",
        enableGrouping: true,
        getValue: (row) => describeDuplicateOrigin(row),
        renderCell({ row }) {
          const created = row.original.CreatedAt
            ? formatDateString(row.original.CreatedAt)
            : null
          return (
            <span className="inline-flex max-w-full items-baseline gap-1.5">
              <span className="truncate">
                {describeDuplicateOrigin(row.original)}
              </span>
              {created && (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {created}
                </span>
              )}
            </span>
          )
        },
      },
      Hubspot: {
        id: "Hubspot",
        label: "HubSpot",
        getValue: (row) => (row.HasHubspotContact ? "Linked" : "Not linked"),
        renderCell({ row }) {
          return row.original.HasHubspotContact ? (
            <DotText variant="success-1">Linked</DotText>
          ) : (
            <span className="text-muted-foreground">Not linked</span>
          )
        },
      },
      Keep: {
        id: "Keep",
        label: "Keep",
        description: KEEP_DESCRIPTION,
        enableSorting: false,
        getValue: (row) => (suggestedIds.has(row.Id) ? "Suggested" : ""),
        renderCell({ row }) {
          return (
            <UserEmailDuplicateKeepCell
              row={row.original}
              others={othersById.get(row.original.Id) ?? []}
              isSuggested={suggestedIds.has(row.original.Id)}
              onMerged={onMerged}
            />
          )
        },
      },
    },
  })
}

export type UserEmailDuplicateTableProps = Omit<
  TableProps<UserEmailDuplicateFragment>,
  "data" | "columns"
>

/**
 * The Duplicate Emails worklist: every live user whose email another live
 * user holds too, grouped by email, each group two (or more) rows side by
 * side with what they carry - logins, sign-ins, teams, origin, CRM link -
 * and a Keep button per row that merges the others into it. Owns the
 * query, shared through the Apollo cache with the tab trigger's count.
 */
function UserEmailDuplicateTable(props: UserEmailDuplicateTableProps) {
  const { className, ...tableProps } = props

  const { data, loading, error, refetch } = useUserEmailDuplicateQuery()
  const rows = data?.getUserEmailDuplicates ?? []

  const { othersById, suggestedIds, groupCount } = useMemo(() => {
    const othersById = new Map<number, UserEmailDuplicateFragment[]>()
    const suggestedIds = new Set<number>()
    const groups = groupDuplicatesByEmail(rows)
    for (const group of groups) {
      const keeper = suggestKeeper(group)
      if (keeper) suggestedIds.add(keeper.Id)
      for (const row of group) {
        othersById.set(
          row.Id,
          group.filter((other) => other.Id !== row.Id),
        )
      }
    }
    return { othersById, suggestedIds, groupCount: groups.length }
  }, [rows])

  const columns = useMemo(
    () =>
      createUserEmailDuplicateColumns({
        othersById,
        suggestedIds,
        onMerged() {
          void refetch()
        },
      }),
    [othersById, suggestedIds, refetch],
  )

  return (
    <Table
      className={cn("user-email-duplicate-table", className)}
      tableId="user-email-duplicate-table"
      data={rows}
      columns={columns}
      initialColumnSizing={COLUMN_SIZING}
      initialGrouping={["Email"]}
      initialSorting={[{ id: "Email", desc: false }]}
      enableGrouping
      {...tableProps}
    >
      <Table.Toolbar>
        <span className="text-sm text-muted-foreground">
          {loading
            ? "Loading..."
            : `${groupCount} email${groupCount === 1 ? "" : "s"} held by more than one user`}
        </span>
        <div className="flex-1" />
        <Table.Search placeholder="Search by email or name..." />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => {
            void refetch()
          }}
          exportFileName="user-email-duplicates"
        />
      </Table.Toolbar>

      <Table.FilterList />
      <Table.GroupList />

      <Table.Content countLabel="users" isLoading={loading} error={error} />
    </Table>
  )
}

export default UserEmailDuplicateTable
